import { useState } from 'react';
import { AxiosError } from 'axios';
import api from '../utils/api';

interface ApiResponse<T = any> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

interface UseApiOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

interface ApiErrorResponse {
  message: string;
}

export const useApi = (options: UseApiOptions = {}) => {
  const [state, setState] = useState<ApiResponse>({
    data: null,
    error: null,
    loading: false,
  });

  const request = async <T>(
    method: string,
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> => {
    console.log('[useApi] === Starting API request ===');
    console.log('[useApi] Method:', method);
    console.log('[useApi] Endpoint:', endpoint);
    console.log('[useApi] Data:', data);
    console.log('[useApi] Headers:', headers);
    
    try {
      console.log('[useApi] Setting loading state to true');
      setState(prev => ({ ...prev, loading: true, error: null }));

      console.log('[useApi] About to call axios with config:', {
        method,
        url: endpoint,
        hasData: !!data,
        hasHeaders: !!headers
      });

      const response = await api({
        method,
        url: endpoint,
        data,
        headers: {
          ...headers,
        },
      });

      console.log('[useApi] Axios call completed successfully');
      console.log('[useApi] Response status:', response.status);
      console.log('[useApi] Response data:', response.data);
      console.log('[useApi] Response headers:', response.headers);

      setState(prev => ({
        ...prev,
        data: response.data,
        loading: false,
        error: null,
      }));

      console.log('[useApi] State updated with response data');
      
      if (options.onSuccess) {
        console.log('[useApi] Calling onSuccess callback');
        options.onSuccess(response.data);
      }

      console.log('[useApi] Returning success response');
      return {
        data: response.data,
        error: null,
        loading: false,
      };
    } catch (err) {
      console.error('[useApi] === Axios request failed ===');
      console.error('[useApi] Error caught:', err);
      console.error('[useApi] Error type:', typeof err);
      
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('[useApi] Axios error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        hasResponse: !!error.response,
        hasRequest: !!error.request,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        'An error occurred';

      console.error('[useApi] Final error message:', errorMessage);

      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        loading: false 
      }));
      
      if (options.onError) {
        console.error('[useApi] Calling onError callback');
        options.onError(errorMessage);
      }

      console.error('[useApi] Returning error response');
      return { data: null, error: errorMessage, loading: false };
    }
  };

  const get = <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>('GET', endpoint, undefined, headers);

  const post = <T>(endpoint: string, data?: any, headers?: Record<string, string>) =>
    request<T>('POST', endpoint, data, headers);

  const put = <T>(endpoint: string, data?: any, headers?: Record<string, string>) =>
    request<T>('PUT', endpoint, data, headers);

  const del = <T>(endpoint: string, headers?: Record<string, string>) =>
    request<T>('DELETE', endpoint, undefined, headers);

  return {
    ...state,
    get,
    post,
    put,
    delete: del,
  };
};

export default useApi;
